import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Heading from 'components/shared/Heading';
import ButtonBase from 'components/shared/ButtonBase';
import Spinner from 'components/shared/Spinner';
import { getURLParam } from 'helpers/url';
import { createLogger } from 'helpers/logger';
import { retryWhileNotTrue } from 'helpers/retry';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import ERRORS from 'constants/errors';
import { openIDLoginSignup } from 'reduxConfig/auth.creators';
import { OPEN_ID_PROVIDERS, OPEN_ID_LOGIN_HANDLERS, URL_PARAMS } from 'constants/openIdProviders';
import styles from './MobileAppLoginRedirect.module.scss';

const log = createLogger('[social login][web sso redirect]');

const FIREBASE_INIT_DELAY_MS = 500;
const OPENID_LOGIN_IN_PROGRESS = 'auth:openIDLoginInProgress';
const REFERRAL_CODE = 'auth:referralCode';
const CARD_PARTNERSHIP_CODE = 'auth:cardPartnershipCode';
const RETRY_GET_REDIRECT_RESULT = 'auth:retryGetRedirectResult';

const cleanStorage = () => {
  NBXStorage.removeItem(RETRY_GET_REDIRECT_RESULT);
  NBXStorage.removeItem(OPENID_LOGIN_IN_PROGRESS);
};

const LOGIN_APP_PATH = () =>
  `com.nbx.exchange://app.nbx.com${window.MICROFRONTENDS_CONFIG.routes.login?.path}`;
export function MobileAppLoginRedirect({ dispatch }) {
  const { t } = useTranslation();

  const [appRedirectUrl, setAppRedirectUrl] = useState();
  const [isShowRedirectButton, setIsShowRedirectButton] = useState(false);

  const [isOpenIdLoginInProgress, setIsOpenIdLoginInProgress] = useState();

  const [firebaseUserCredential, setFirebaseUserCredential] = useState();
  const [firebaseError, setFirebaseError] = useState();

  const [referral, setReferral] = useState();
  const [cardPartnership, setCardPartnership] = useState();

  useEffect(() => {
    NBXStorage.getItem(OPENID_LOGIN_IN_PROGRESS).then(setIsOpenIdLoginInProgress);
    NBXStorage.getItem(REFERRAL_CODE).then(setReferral);
    NBXStorage.getItem(CARD_PARTNERSHIP_CODE).then(setCardPartnership);
  }, []);

  const redirectToIdpLoginPage = useCallback(
    async (provider, referralCode, cardPartnershipCode) => {
      if (Object.values(OPEN_ID_PROVIDERS).includes(provider) && provider in OPEN_ID_LOGIN_HANDLERS) {
        if (window.FIREBASE.isRedirectFlow()) {
          setTimeout(async () => {
            await NBXStorage.setItem(OPENID_LOGIN_IN_PROGRESS, provider);
            dispatch(
              openIDLoginSignup(
                OPEN_ID_LOGIN_HANDLERS[provider],
                referralCode || null,
                cardPartnershipCode || null
              )
            );
          }, FIREBASE_INIT_DELAY_MS);
        } else {
          setIsOpenIdLoginInProgress(true);
          try {
            const userCredential = await OPEN_ID_LOGIN_HANDLERS[provider]();
            if (userCredential) setFirebaseUserCredential(userCredential);
            else {
              setFirebaseError(new Error('Cannot get redirect result from popup idp'));
            }
          } catch (e) {
            setFirebaseError(`Cannot do popup auth: ${e.message}`);
          }
        }
      } else {
        log.warn('there is no such provider');
      }
    },
    [dispatch]
  );

  const handleSsoRedirectFromApp = useCallback(async () => {
    const provider = getURLParam(URL_PARAMS.loginWith);
    if (provider) {
      const referralCode = getURLParam(URL_PARAMS.referralCode);
      log.info('[referralCode] get referralCode from the redirected app url', { referralCode });
      if (referralCode) {
        setReferral(referralCode);
        await NBXStorage.setItem(REFERRAL_CODE, referralCode);
      }

      const cardPartnershipCode = getURLParam(URL_PARAMS.cardPartnershipCode);
      log.info('[cardPartnershipCode] get cardPartnershipCode from the redirected app url', {
        cardPartnershipCode
      });
      if (cardPartnershipCode) {
        setCardPartnership(cardPartnershipCode);
        await NBXStorage.setItem(CARD_PARTNERSHIP_CODE, cardPartnershipCode);
      }

      redirectToIdpLoginPage(provider, referralCode, cardPartnershipCode);
    }
  }, [redirectToIdpLoginPage]);

  const handleOnAuthStateChanged = useCallback(async (userCredential, referralCode, cardPartnershipCode) => {
    const idToken = await window.FIREBASE.getIdToken();
    const params = new URLSearchParams();
    params.append(URL_PARAMS.verifyLoginIdtoken, idToken);
    params.append(URL_PARAMS.verifyLoginEmail, userCredential.user.email);
    if (referralCode) params.append(URL_PARAMS.referralCode, referralCode);
    if (cardPartnershipCode) params.append(URL_PARAMS.cardPartnershipCode, cardPartnershipCode);
    const url = `${LOGIN_APP_PATH()}?${params.toString()}`;
    setAppRedirectUrl(url);
    cleanStorage();
  }, []);

  const handleOnAuthStateChangedError = useCallback(error => {
    log.error('handleOnAuthStateChangedError:', { error });
    const params = new URLSearchParams();
    params.append(URL_PARAMS.loginError, ERRORS.login);
    const url = `${LOGIN_APP_PATH()}?${params.toString()}`;
    window.FIREBASE.logout().catch(errorLogout =>
      log.error('handleOnAuthStateChangedError, error logout:', { errorLogout })
    );
    setAppRedirectUrl(url);
    cleanStorage();
  }, []);

  useEffect(() => {
    let unsubscribe;
    const subscribeToCallbackFromIdp = async () => {
      unsubscribe = await window.FIREBASE.onAuthStateChanged(async (user, error) => {
        if (error) setFirebaseError(error);
        else if (isOpenIdLoginInProgress && isOpenIdLoginInProgress !== undefined) {
          try {
            const getRedirectResultWhileNotTrue = retryWhileNotTrue(
              window.FIREBASE.getRedirectResult,
              2,
              1000
            );
            const userCredential = await getRedirectResultWhileNotTrue();
            if (userCredential) setFirebaseUserCredential(userCredential);
            else setFirebaseError(new Error('Cannot get redirect result from idp, userCredential is empty'));
          } catch (e) {
            setFirebaseError(new Error(`Cannot get redirect result from idp ${e?.message}`));
          }
        }
      });
    };

    if (window.PLATFORM === 'web' && window.FIREBASE.isRedirectFlow()) subscribeToCallbackFromIdp();

    return unsubscribe;
  }, [handleOnAuthStateChanged, isOpenIdLoginInProgress, redirectToIdpLoginPage]);

  useEffect(() => {
    if (!isOpenIdLoginInProgress && isOpenIdLoginInProgress !== undefined) handleSsoRedirectFromApp();
  }, [handleSsoRedirectFromApp, isOpenIdLoginInProgress, redirectToIdpLoginPage]);

  useEffect(() => {
    if (
      isOpenIdLoginInProgress &&
      isOpenIdLoginInProgress !== undefined &&
      firebaseUserCredential &&
      referral !== undefined &&
      cardPartnership !== undefined
    ) {
      handleOnAuthStateChanged(firebaseUserCredential, referral, cardPartnership);
    }
  }, [handleOnAuthStateChanged, isOpenIdLoginInProgress, firebaseUserCredential, referral, cardPartnership]);

  useEffect(() => {
    if (isOpenIdLoginInProgress && isOpenIdLoginInProgress !== undefined && firebaseError)
      handleOnAuthStateChangedError(firebaseError);
  }, [handleOnAuthStateChangedError, isOpenIdLoginInProgress, firebaseError]);

  const redirectToLocation = useCallback(url => (window.location.href = url), []);

  useEffect(() => {
    if (appRedirectUrl) {
      redirectToLocation(appRedirectUrl);
      const timeout = setTimeout(() => setIsShowRedirectButton(true), 1000);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [appRedirectUrl, redirectToLocation]);

  return (
    <div className={styles.mobAppRedirect}>
      <img src="/assets/images/logo-green.png" alt="NBX" />
      <div className={styles.main}>
        {isOpenIdLoginInProgress && <Heading tag="h2">{t('auth_login_openign_nbx')}...</Heading>}
      </div>
      <ButtonBase
        noTarget
        onClick={() => redirectToLocation(appRedirectUrl)}
        disabled={!appRedirectUrl}
        fullWidth>
        {isShowRedirectButton ? <span className={styles.text}>{t('common_continue')}</span> : <Spinner />}
      </ButtonBase>
    </div>
  );
}

MobileAppLoginRedirect.propTypes = {
  dispatch: PropTypes.func.isRequired
};

MobileAppLoginRedirect.defaultProps = {};
